import { BackgroundJobRunStatus, BackgroundJobRunStatusLabels, type InputData, URLKeys } from 'noddi-async';
import { enrichFiltersWithCounts, filterUnitFor, mapFilterControls, useFilterUnits } from 'noddi-ui-web';
import { entriesToOptions } from 'noddi-util';

import { CREATED_AT_FILTER_TEXT } from '../Date/useCreatedAtFilter';
import { backgroundJobFacetFilter } from './backgroundJobFacetFilter';
import { useBackgroundJobFilterOptions } from './useBackgroundJobFilterOptions';

const JOB_STATUS_OPTIONS = entriesToOptions(BackgroundJobRunStatus, BackgroundJobRunStatusLabels);

type BackgroundJobRunFilterInput = Omit<
  InputData<typeof URLKeys.getPaginatedBackgroundJobRuns>,
  'pageIndex' | 'pageSize' | 'order' | 'ordering' | 'search'
>;

const jobRunFilterUnit = filterUnitFor<BackgroundJobRunFilterInput>();

const statusFilter = jobRunFilterUnit.multiSelect('statusIn', {
  title: 'Status',
  description: 'Filter by task run status',
  options: JOB_STATUS_OPTIONS,
  exclude: true
});

export const useJobsTableFilters = ({
  adapterRelated = false,
  taskNames
}: {
  /** Scopes the list to adapter-related tasks. */
  adapterRelated?: boolean;
  /** Page-fixed task names; replaces the Task filter. */
  taskNames?: string[];
} = {}) => {
  const { taskNameOptions, queueOptions, workerOptions, statusCounts, load, withSelected } =
    useBackgroundJobFilterOptions();

  const task = backgroundJobFacetFilter({
    key: 'taskNameIn',
    title: 'Task',
    description: 'Filter by one or more task names',
    options: taskNameOptions,
    load,
    withSelected
  });
  const queue = backgroundJobFacetFilter({
    key: 'queueIn',
    title: 'Queue',
    description: 'Filter by one or more broker queues',
    options: queueOptions,
    load,
    withSelected
  });
  const worker = backgroundJobFacetFilter({
    key: 'workerIn',
    title: 'Worker',
    description: 'Filter by one or more worker hostnames',
    options: workerOptions,
    load,
    withSelected
  });

  const filters = useFilterUnits([
    ...(adapterRelated ? [jobRunFilterUnit.locked({ adapterRelated: true })] : []),
    taskNames
      ? jobRunFilterUnit.locked({ taskNameIn: taskNames })
      : jobRunFilterUnit.compose('taskNameIn', task.urlParams, (values, setValues) => {
          const { control, include, exclude } = task.resolve(values, setValues);
          return { controls: control, params: { taskNameIn: include, excludedTaskNameIn: exclude } };
        }),
    statusFilter,
    jobRunFilterUnit.compose('queueIn', queue.urlParams, (values, setValues) => {
      const { control, include, exclude } = queue.resolve(values, setValues);
      return { controls: control, params: { queueIn: include, excludedQueueIn: exclude } };
    }),
    jobRunFilterUnit.compose('workerIn', worker.urlParams, (values, setValues) => {
      const { control, include, exclude } = worker.resolve(values, setValues);
      return { controls: control, params: { workerIn: include, excludedWorkerIn: exclude } };
    }),
    jobRunFilterUnit.numberRange('retries', {
      title: 'Retries',
      description: 'Filter by retry count',
      minLabel: 'Min retries',
      maxLabel: 'Max retries',
      exclude: true
    }),
    jobRunFilterUnit.numberRange('durationSeconds', {
      title: 'Duration (seconds)',
      description: 'Filter by run duration in seconds (finished − started)',
      minLabel: 'Min seconds',
      maxLabel: 'Max seconds',
      exclude: true
    }),
    jobRunFilterUnit.dateRange('startedAt', {
      title: 'Started at',
      description: 'Filter by when the task started',
      exclude: true
    }),
    jobRunFilterUnit.dateRange('finishedAt', {
      title: 'Finished at',
      description: 'Filter by when the task finished',
      exclude: true
    }),
    jobRunFilterUnit.dateRange('createdAt', {
      ...CREATED_AT_FILTER_TEXT,
      description: 'Filter by when the task run was first recorded',
      exclude: true
    })
  ]);

  // Status counts come from the facet counts; the options stay the full status list.
  const withCounts = mapFilterControls(
    filters,
    (control) => enrichFiltersWithCounts([control], { statusIn: statusCounts })[0] ?? control
  );
  const statusControl = filters.controlOf(statusFilter);

  return {
    filters: withCounts,
    /** Scopes the list to these statuses (a "Running only" shortcut), clearing status exclusions. */
    setStatusIn: (values: BackgroundJobRunStatus[]) => {
      statusControl?.onChange({ include: values, exclude: [] });
    }
  };
};
